/**
 * R-10 (independent recheck, 2026-10-01) — persistence for measurement
 * drafts that failed to save (network, offline-durable-queue, or a
 * response-bearing server/validation error — see B2-07) so they
 * outlive the 7-second toast that used to be the only place they
 * lived.
 *
 * B2-02/03/04 (independent recheck BATCH 2, 2026-10-01) reopened this:
 *   - B2-02: the original single global `localStorage` key meant ANY
 *     signed-in user on this device/browser could load whatever the
 *     PREVIOUS user's failed drafts were. Every read/write here now
 *     takes an explicit `identityKey` (`${tenant_id}:${user_id}`) and
 *     is namespaced by it — a different identity simply never sees
 *     another identity's key. Logging out does NOT delete anything
 *     (the owner's unsaved work must survive a logout/login cycle on
 *     the same device), it just stops being reachable by anyone who
 *     isn't that same identity.
 *   - B2-03: if `localStorage.setItem` ALSO throws (quota exceeded,
 *     private-mode Safari, etc.), the draft used to live ONLY in the
 *     calling React component's state — gone the instant that
 *     component unmounts (any real navigation away). `_memoryFallback`
 *     is a module-level Map (not component state), so it survives for
 *     as long as this JS page/tab is alive, independent of which
 *     component is currently mounted. It does NOT survive an actual
 *     page reload or crash — every write reports whether it actually
 *     reached durable storage (`persisted: true/false`) so the UI can
 *     say so honestly instead of implying a guarantee that isn't real.
 *   - B2-04: the old API took a caller-held `prev` array and blindly
 *     overwrote storage with `[...prev, newItem]`. Two components (or
 *     two tabs) each holding a stale snapshot could clobber each
 *     other's single-item change. Every write below re-reads the
 *     CURRENT persisted state immediately before merging in exactly
 *     one add/remove — but a fresh read-then-write is NOT atomic
 *     across two different tabs (see B3-04 below); it only prevents a
 *     caller from using a snapshot that was ALREADY stale when it was
 *     captured. `subscribeToFailedDrafts` also listens for the
 *     browser's `storage` event so a change made in ANOTHER tab is
 *     reflected into this one without a reload.
 *
 * B3-04 (2026-10-01 completion directive) reopened this again: a fresh
 * read immediately before a write is still two separate synchronous
 * calls, each safe only against races WITHIN its own tab. Two REAL
 * browser tabs are two separate JS execution contexts; nothing stops
 * tab B's read+write from landing in the gap between tab A's read and
 * tab A's own write. Reproduced exactly as described: tab A reads
 * `[]`, tab B reads `[]` and writes `[B]`, tab A (still holding its
 * stale `[]` read) writes `[A]` — `B` is gone even though both tabs
 * only ever added, never removed, anything. A `storage` event listener
 * fixes the SYMPTOM (another tab eventually sees the right final
 * state) but not the LOST UPDATE itself.
 *
 * Fixed with the Web Locks API (`navigator.locks`), which is the
 * platform's own purpose-built mechanism for exactly this — true
 * mutual exclusion across separate tabs/workers of the same origin,
 * backed by the browser process itself, not by anything storable in
 * `localStorage`. Every mutation now runs inside
 * `navigator.locks.request(<per-identity lock name>, cb)`; only one
 * tab's callback can be running at a time for a given identity, so the
 * read-merge-write cycle below is genuinely atomic across tabs. Where
 * `navigator.locks` isn't available (jsdom's test environment has no
 * Web Locks implementation at all; some older browsers), an optimistic
 * compare-and-swap retry loop is used instead: the raw localStorage
 * string is re-checked for an exact match immediately before the
 * write, and the whole read-merge-write is retried against the fresh
 * value if anything changed underneath it — this cannot fully
 * eliminate the race the way a real lock does (two separate processes
 * can still land their check+write in the same instant), but it turns
 * "always silently clobbers" into "only loses under a true
 * microsecond-scale collision", and is the best available fallback
 * without a lock primitive.
 */
const KEY_PREFIX = "forgedops:failed_measurement_drafts:v2:";

// B2-03 — module-level (NOT React state), one in-memory list per
// identity, used only when a real localStorage write fails.
const _memoryFallback = new Map();
// B3-07 (2026-10-01 completion directive) — `subscribeToFailedDrafts`
// below only ever caught ANOTHER tab's writes (the browser's own
// `storage` event never fires in the document that made the write).
// A component calling `upsertFailedDraft` already gets the fresh list
// back directly and updates its own state — but nothing told any
// OTHER currently-mounted consumer (e.g. an app-wide "unsaved work"
// banner that has to keep working after the sheet viewer itself
// unmounts) that a same-tab write just happened. This is a tiny
// same-tab pub-sub, independent of the cross-tab `storage` listener,
// so a single `subscribeToFailedDrafts` call covers both.
const _sameTabListeners = new Map(); // identityKey -> Set<callback>

function _notifySameTab(identityKey, drafts) {
  const set = _sameTabListeners.get(identityKey);
  if (set) set.forEach((cb) => cb(drafts));
}

// Identities whose MOST RECENT write attempt failed. While an identity
// is degraded, reads prefer the in-memory fallback over real storage —
// otherwise a failed write leaves real storage legitimately empty,
// which would otherwise look indistinguishable from "nothing was ever
// saved" and mask the very data the fallback exists to protect.
// Cleared the moment a write for that identity succeeds again.
const _degraded = new Set();

function _storageKey(identityKey) {
  return `${KEY_PREFIX}${identityKey}`;
}

// B3-04 — serializes every mutation for a given identity so a
// read-merge-write cycle can never interleave with another tab's.
// `navigator.locks` gives TRUE cross-tab exclusion where available;
// the CAS loop below is the fallback where it isn't (see file-level
// comment). `mutate(currentDrafts) => nextDrafts` is called with the
// FRESHEST possible read, inside the lock.
async function _withIdentityLock(identityKey, mutate) {
  if (typeof navigator !== "undefined" && navigator.locks && typeof navigator.locks.request === "function") {
    return navigator.locks.request(`forgedops:failed_drafts_lock:${identityKey}`, () => _casApply(identityKey, mutate));
  }
  return _casApply(identityKey, mutate);
}

// Optimistic compare-and-swap: re-reads the raw stored string
// immediately before writing and retries the whole read-merge-write
// cycle (against the NEW current value) if it changed underneath us.
// Without `navigator.locks` this is the best available guard — it
// cannot make the race impossible (two separate browser processes can
// still land their own check+write in the same instant), but it turns
// "always silently clobbers" into "only loses on a true
// microsecond-scale collision" instead of every single overlap.
function _casApply(identityKey, mutate, maxAttempts = 25) {
  const key = _storageKey(identityKey);
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let raw;
    try {
      raw = window.localStorage.getItem(key);
    } catch {
      // Storage unreadable at all (private mode, disabled) — fall
      // back to the in-memory list for this identity.
      const nextDrafts = mutate(_memoryFallback.get(identityKey) || []);
      _memoryFallback.set(identityKey, nextDrafts);
      _degraded.add(identityKey);
      return { drafts: nextDrafts, persisted: false };
    }
    let currentDrafts;
    try {
      const parsed = raw ? JSON.parse(raw) : [];
      currentDrafts = Array.isArray(parsed) ? parsed : [];
    } catch {
      currentDrafts = [];
    }
    const nextDrafts = mutate(currentDrafts);
    try {
      if (window.localStorage.getItem(key) !== raw) {
        continue; // someone else wrote between our read and now — retry fresh
      }
      window.localStorage.setItem(key, JSON.stringify(nextDrafts));
      _memoryFallback.set(identityKey, nextDrafts);
      _degraded.delete(identityKey);
      return { drafts: nextDrafts, persisted: true };
    } catch {
      _memoryFallback.set(identityKey, nextDrafts);
      _degraded.add(identityKey);
      return { drafts: nextDrafts, persisted: false };
    }
  }
  // Exhausted retries under sustained contention — apply against the
  // in-memory fallback rather than silently dropping the operation.
  const nextDrafts = mutate(_memoryFallback.get(identityKey) || []);
  _memoryFallback.set(identityKey, nextDrafts);
  _degraded.add(identityKey);
  return { drafts: nextDrafts, persisted: false };
}

export function loadFailedDrafts(identityKey) {
  if (!identityKey) return [];
  if (_degraded.has(identityKey)) {
    return _memoryFallback.get(identityKey) || [];
  }
  try {
    const raw = window.localStorage.getItem(_storageKey(identityKey));
    // `getItem` succeeded — this IS the authoritative state, even if
    // it's null (nothing saved) or malformed (defensively treated as
    // empty).
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return _memoryFallback.get(identityKey) || [];
  }
}

// Test-only: reset module-level state between test cases that reuse
// the same identityKey. Not used by any application code path.
export function _resetFailedDraftsStateForTests() {
  _memoryFallback.clear();
  _degraded.clear();
  _sameTabListeners.clear();
}

// B2-04/B3-04 — the whole read-merge-write cycle runs inside
// `_withIdentityLock` so it is atomic across tabs, not just safe
// against a caller's own stale snapshot. Returns { drafts, persisted }.
// `persisted` is also stamped onto the draft itself (B2-03) so the UI
// can show "not guaranteed to survive a reload" per-item, honestly,
// instead of implying every visible draft is equally safe.
export async function upsertFailedDraft(identityKey, payload, targetSheetId, reason) {
  let persistedFlag = true;
  const result = await _withIdentityLock(identityKey, (current) => {
    const base = current.filter((d) => d.payload.client_op_id !== payload.client_op_id);
    return [...base, {
      id: payload.client_op_id, sheetId: targetSheetId, payload, reason,
      savedAt: new Date().toISOString(), persisted: true,
    }];
  });
  persistedFlag = result.persisted;
  // The lock-protected mutate callback above has to optimistically
  // stamp `persisted: true` before it knows whether the write actually
  // reached durable storage. Correct that single entry's own flag in
  // memory from the ACTUAL result — no second storage round-trip,
  // which would otherwise re-read from (still-failing) real storage
  // instead of the in-memory fallback that now holds the true state.
  const drafts = persistedFlag
    ? result.drafts
    : result.drafts.map((d) => (d.id === payload.client_op_id ? { ...d, persisted: false } : d));
  if (!persistedFlag) _memoryFallback.set(identityKey, drafts);
  _notifySameTab(identityKey, drafts);
  return { drafts, persisted: persistedFlag };
}

export async function removeFailedDraft(identityKey, id) {
  const result = await _withIdentityLock(identityKey, (current) => current.filter((d) => d.id !== id));
  _notifySameTab(identityKey, result.drafts);
  return { drafts: result.drafts, persisted: result.persisted };
}

// B2-04 — cross-tab sync. The browser only fires `storage` in OTHER
// documents than the one that made the write, which is exactly what's
// needed here: this tab's own writes already update its React state
// directly; this listener catches writes made by a DIFFERENT tab (or
// a second mounted viewer) under the SAME identity.
export function subscribeToFailedDrafts(identityKey, onChange) {
  if (typeof window === "undefined" || !identityKey) return () => {};
  const key = _storageKey(identityKey);
  const handler = (e) => {
    if (e.key === key) onChange(loadFailedDrafts(identityKey));
  };
  window.addEventListener("storage", handler);
  if (!_sameTabListeners.has(identityKey)) _sameTabListeners.set(identityKey, new Set());
  _sameTabListeners.get(identityKey).add(onChange);
  return () => {
    window.removeEventListener("storage", handler);
    const set = _sameTabListeners.get(identityKey);
    if (set) {
      set.delete(onChange);
      if (set.size === 0) _sameTabListeners.delete(identityKey);
    }
  };
}
